import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useConstants } from '../../context/ConstantsContext';
import { useStore } from '../../context/StoreContext';
import categoryService from '../../services/categoryService';
import inventoryService from '../../services/inventoryService';
import { toApiDateValue } from '../../utils/dateUtils';

const DEFAULT_APPLIES_TO = 'SPECIFIC_PRODUCTS';

const buildDefaultSlab = () => ({
  minThreshold: 0,
  maxThreshold: '',
  rewardType: 'PERCENTAGE',
  rewardValue: '',
  rewardApplication: 'PER_ORDER',
});

const buildInitialFormData = (constants) => ({
  name: '',
  description: '',
  type: constants.PROMOTION_TYPES.PERCENTAGE_ITEM,
  value: {
    discountValue: '',
    multiBuyDiscountType: 'FIXED_PRICE',
    multiBuyBuyQuantity: 2,
    multiBuyGetQuantity: 1,
    slabBasis: 'ORDER_VALUE',
    slabs: [buildDefaultSlab()],
  },
  conditions: {
    appliesTo: DEFAULT_APPLIES_TO,
    productIds: [],
    categories: [],
    subcategories: [],
    minPurchaseAmount: 0,
  },
  startDate: toApiDateValue(new Date()),
  endDate: '',
  isActive: true,
  isStackable: false,
  applicationMode: 'USER_DRIVEN',
  stores: [],
});

const toNullableNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : NaN;
};

const normalizeSlabs = (slabs = []) => slabs.map((slab) => ({
  minThreshold: Number(slab.minThreshold),
  maxThreshold: toNullableNumber(slab.maxThreshold),
  rewardType: slab.rewardType,
  rewardValue: Number(slab.rewardValue),
  rewardApplication: slab.rewardApplication || 'PER_ORDER',
}));

const hasEntries = (values) => Array.isArray(values) && values.length > 0;

const validateSlabs = ({ type, value, conditions }) => {
  const errors = [];
  const gapMessages = [];

  if (hasEntries(conditions?.subcategories) && conditions?.appliesTo !== 'SPECIFIC_CATEGORIES') {
    errors.push('Subcategories can only be selected for category-scoped promotions.');
  }
  if (conditions?.appliesTo === 'SPECIFIC_CATEGORIES' && !hasEntries(conditions?.categories)) {
    errors.push('Select at least one category for this promotion.');
  }

  if (type !== 'SLAB') {
    return { errors: Array.from(new Set(errors)), gapMessages };
  }

  const slabBasis = value?.slabBasis;
  const slabs = Array.isArray(value?.slabs) ? value.slabs : [];
  const normalized = normalizeSlabs(slabs);

  if (!slabBasis) {
    errors.push('Select a slab basis.');
  }
  if (normalized.length === 0) {
    errors.push('Add at least one slab.');
  }
  if (
    slabBasis === 'ITEM_QUANTITY'
    && !['SPECIFIC_PRODUCTS', 'SPECIFIC_CATEGORIES'].includes(conditions?.appliesTo)
  ) {
    errors.push('Item quantity slabs must apply to specific products or specific categories.');
  }
  if (conditions?.appliesTo === 'SPECIFIC_PRODUCTS' && !hasEntries(conditions?.productIds)) {
    errors.push('Select at least one product for this slab promotion.');
  }
  for (let index = 0; index < normalized.length; index += 1) {
    const current = normalized[index];
    const rowNumber = index + 1;
    if (!Number.isFinite(current.minThreshold) || current.minThreshold < 0) {
      errors.push(`Slab ${rowNumber}: minimum threshold must be 0 or greater.`);
    }
    if (current.maxThreshold != null && (!Number.isFinite(current.maxThreshold) || current.maxThreshold <= current.minThreshold)) {
      errors.push(`Slab ${rowNumber}: maximum threshold must be greater than the minimum threshold.`);
    }
    if (!Number.isFinite(current.rewardValue) || current.rewardValue <= 0) {
      errors.push(`Slab ${rowNumber}: reward value must be greater than 0.`);
    }
    if (current.rewardType === 'PERCENTAGE' && current.rewardValue > 100) {
      errors.push(`Slab ${rowNumber}: percentage reward cannot exceed 100.`);
    }
    if (index > 0) {
      const previous = normalized[index - 1];
      if (current.minThreshold < previous.minThreshold) {
        errors.push('Slabs must be sorted in ascending threshold order.');
      }
      if (previous.maxThreshold == null) {
        errors.push(`Slab ${rowNumber} overlaps an open-ended slab above it.`);
      } else if (current.minThreshold < previous.maxThreshold) {
        // Upper bounds are exclusive, so a slab may start exactly where the
        // previous one ended (that is contiguous, not an overlap). See #208.
        errors.push(`Slab ${rowNumber} overlaps with the previous slab.`);
      } else if (current.minThreshold > previous.maxThreshold + 1e-9) {
        gapMessages.push(
          `No discount between ${previous.maxThreshold} and ${current.minThreshold}.`
        );
      }
    }
  }

  return {
    errors: Array.from(new Set(errors)),
    gapMessages,
  };
};

const findMatchingSlab = (value = 0, slabs = []) => {
  const numericValue = Number(value || 0);
  return normalizeSlabs(slabs).find((slab) => (
    numericValue >= slab.minThreshold
    && (slab.maxThreshold == null || numericValue < slab.maxThreshold)
  )) || null;
};

const calculateSlabPreview = (basisValue, slabs) => {
  const matched = findMatchingSlab(basisValue, slabs);
  if (!matched) {
    return null;
  }
  const numericBasisValue = Number(basisValue || 0);
  if (matched.rewardType === 'PERCENTAGE') {
    return {
      matched,
      discountAmount: (numericBasisValue * matched.rewardValue) / 100,
    };
  }
  if (matched.rewardApplication === 'PER_UNIT') {
    return {
      matched,
      discountAmount: matched.rewardValue * numericBasisValue,
    };
  }
  return {
    matched,
    discountAmount: matched.rewardValue,
  };
};

const formatThresholdRange = (slab) => (
  slab.maxThreshold == null || slab.maxThreshold === ''
    ? `${slab.minThreshold}+`
    : `${slab.minThreshold}-${slab.maxThreshold}`
);

const formatSlabReward = (slab) => {
  if (slab.rewardType === 'PERCENTAGE') {
    return `${slab.rewardValue}% off`;
  }
  return slab.rewardApplication === 'PER_UNIT'
    ? `$${Number(slab.rewardValue || 0).toFixed(2)} off each`
    : `$${Number(slab.rewardValue || 0).toFixed(2)} off order`;
};

const PromotionForm = ({ promotion, stores, onSave, onCancel, isSaving = false }) => {
  const { constants } = useConstants();
  const { activeStore } = useStore();
  const [formData, setFormData] = useState(() => buildInitialFormData(constants));
  const [productQuery, setProductQuery] = useState('');
  const [productResults, setProductResults] = useState([]);
  const [productLoading, setProductLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [buyGetValue, setBuyGetValue] = useState('2+1');
  const [formError, setFormError] = useState('');
  const [slabPreviewInput, setSlabPreviewInput] = useState('250');

  useEffect(() => {
    setFormData(buildInitialFormData(constants));
  }, [constants]);

  useEffect(() => {
    if (promotion) {
      const buyQty = Number(promotion.value?.multiBuyBuyQuantity || 2);
      const getQty = Number(promotion.value?.multiBuyGetQuantity || 1);
      setFormData({
        ...buildInitialFormData(constants),
        ...promotion,
        value: {
          ...buildInitialFormData(constants).value,
          ...promotion.value,
          slabs: Array.isArray(promotion.value?.slabs) && promotion.value.slabs.length > 0
            ? promotion.value.slabs.map((slab) => ({
                ...buildDefaultSlab(),
                ...slab,
                maxThreshold: slab.maxThreshold ?? '',
              }))
            : [buildDefaultSlab()],
        },
        conditions: {
          ...buildInitialFormData(constants).conditions,
          ...promotion.conditions,
          productIds: promotion.conditions?.productIds || [],
          categories: promotion.conditions?.categories || [],
          subcategories: promotion.conditions?.subcategories || [],
        },
        applicationMode: promotion.applicationMode || 'USER_DRIVEN',
        startDate: toApiDateValue(promotion.startDate),
        endDate: toApiDateValue(promotion.endDate),
      });
      setBuyGetValue(`${buyQty}+${getQty}`);
      setSlabPreviewInput(
        promotion.value?.slabBasis === 'ITEM_QUANTITY' ? '12' : '250'
      );
      setProductQuery('');
      setProductResults([]);
      setFormError('');
      return;
    }

    setFormData(buildInitialFormData(constants));
    setBuyGetValue('2+1');
    setSlabPreviewInput('250');
    setProductQuery('');
    setProductResults([]);
    setFormError('');
  }, [promotion, constants]);

  const isFlatOrderType = useMemo(
    () => [
      constants.PROMOTION_TYPES.PERCENTAGE_ORDER,
      constants.PROMOTION_TYPES.FIXED_AMOUNT_ORDER,
    ].includes(formData.type),
    [constants.PROMOTION_TYPES, formData.type]
  );

  useEffect(() => {
    if (isFlatOrderType && formData.conditions.appliesTo !== 'ENTIRE_ORDER') {
      setFormData((prev) => ({
        ...prev,
        conditions: {
          ...prev.conditions,
          appliesTo: 'ENTIRE_ORDER',
          productIds: [],
          categories: [],
          subcategories: [],
        },
      }));
    } else if (!isFlatOrderType && formData.conditions.appliesTo === 'ENTIRE_ORDER' && prevTypeRequiresScope(formData.type, formData.value?.slabBasis)) {
      setFormData((prev) => ({
        ...prev,
        conditions: { ...prev.conditions, appliesTo: DEFAULT_APPLIES_TO },
      }));
    }
  }, [formData.type, formData.conditions.appliesTo, formData.value?.slabBasis, isFlatOrderType]);

  const slabValidation = useMemo(
    () => validateSlabs(formData),
    [formData]
  );

  const slabPreview = useMemo(() => {
    if (formData.type !== 'SLAB') return null;
    const numericValue = Number(slabPreviewInput);
    if (!Number.isFinite(numericValue) || numericValue < 0) return null;
    return calculateSlabPreview(numericValue, formData.value?.slabs || []);
  }, [formData.type, formData.value?.slabs, slabPreviewInput]);

  const selectedProductIds = useMemo(
    () => formData.conditions.productIds || [],
    [formData.conditions.productIds]
  );
  const selectedCategories = useMemo(
    () => formData.conditions.categories || [],
    [formData.conditions.categories]
  );
  const selectedSubcategories = useMemo(
    () => formData.conditions.subcategories || [],
    [formData.conditions.subcategories]
  );
  const activeStoreId = activeStore && activeStore._id !== 'setup' ? activeStore._id : null;

  const subcategoryOptions = useMemo(() => {
    const selectedSet = new Set(selectedCategories);
    const names = new Set();
    categoryOptions
      .filter((category) => selectedSet.has(category.name))
      .forEach((category) => {
        (category.subcategories || []).forEach((subcategory) => {
          if (subcategory) names.add(subcategory);
        });
      });
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [categoryOptions, selectedCategories]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'type' && value === constants.PROMOTION_TYPES.SLAB) {
        next.conditions = {
          ...next.conditions,
          appliesTo: 'ENTIRE_ORDER',
          productIds: [],
          categories: [],
          subcategories: [],
        };
      }
      return next;
    });
    setFormError('');
  };

  const handleNestedChange = (part, event) => {
    const { name, value } = event.target;
    setFormData((prev) => {
      const next = { ...prev, [part]: { ...prev[part], [name]: value } };
      if (part === 'conditions' && name === 'appliesTo') {
        if (value !== 'SPECIFIC_CATEGORIES') {
          next.conditions.categories = [];
          next.conditions.subcategories = [];
        }
        if (value !== 'SPECIFIC_PRODUCTS') {
          next.conditions.productIds = [];
        }
      }
      if (
        part === 'value'
        && name === 'slabBasis'
        && value === 'ORDER_VALUE'
        && next.type === constants.PROMOTION_TYPES.SLAB
        && next.conditions.appliesTo !== 'ENTIRE_ORDER'
        && !hasEntries(next.conditions.productIds)
        && !hasEntries(next.conditions.categories)
      ) {
        next.conditions = { ...next.conditions, appliesTo: 'ENTIRE_ORDER' };
      }
      return next;
    });
    setFormError('');
  };

  useEffect(() => {
    setCategoryLoading(true);
    categoryService.fetchCategories()
      .then((data) => setCategoryOptions(Array.isArray(data) ? data : []))
      .catch(() => setCategoryOptions([]))
      .finally(() => setCategoryLoading(false));
  }, []);

  const handleConditionMultiSelect = (field, event) => {
    const values = Array.from(event.target.selectedOptions, (option) => option.value);
    setFormData((prev) => {
      const nextConditions = { ...prev.conditions, [field]: values };
      if (field === 'categories') {
        const allowedSubcategories = new Set(
          categoryOptions
            .filter((category) => values.includes(category.name))
            .flatMap((category) => category.subcategories || [])
        );
        nextConditions.subcategories = (prev.conditions.subcategories || []).filter((subcategory) => (
          allowedSubcategories.has(subcategory)
        ));
      }
      return { ...prev, conditions: nextConditions };
    });
    setFormError('');
  };

  const handleMultiSelect = (part, event) => {
    const values = Array.from(event.target.selectedOptions, (option) => option.value);
    setFormData((prev) => ({ ...prev, [part]: values }));
    setFormError('');
  };

  const toggleProductSelection = useCallback((product) => {
    if (!product?._id) return;
    setFormData((prev) => {
      const current = prev.conditions.productIds || [];
      const next = current.includes(product._id)
        ? current.filter((id) => id !== product._id)
        : [...current, product._id];
      return { ...prev, conditions: { ...prev.conditions, productIds: next } };
    });
    setSelectedProducts((prev) => {
      const exists = prev.some((entry) => entry._id === product._id);
      if (exists) {
        return prev.filter((entry) => entry._id !== product._id);
      }
      return [...prev, product];
    });
  }, []);

  useEffect(() => {
    const fetchSelectedProducts = async () => {
      if (!promotion || !activeStoreId || !selectedProductIds.length) {
        if (!selectedProductIds.length) {
          setSelectedProducts([]);
        }
        return;
      }
      const uniqueIds = Array.from(new Set(selectedProductIds));
      const results = await Promise.all(
        uniqueIds.map((id) => inventoryService.getProductById(id, activeStoreId).catch(() => null))
      );
      setSelectedProducts(results.filter(Boolean));
    };
    fetchSelectedProducts();
  }, [promotion, activeStoreId, selectedProductIds]);

  useEffect(() => {
    const term = productQuery.trim();
    if (!term) {
      setProductResults([]);
      setProductLoading(false);
      return;
    }
    if (!activeStoreId) {
      setProductResults([]);
      return;
    }
    setProductLoading(true);
    const debounce = setTimeout(async () => {
      try {
        const data = await inventoryService.searchProducts(activeStoreId, term);
        setProductResults(data);
      } catch (err) {
        setProductResults([]);
      } finally {
        setProductLoading(false);
      }
    }, 250);
    return () => clearTimeout(debounce);
  }, [productQuery, activeStoreId]);

  const handleSlabChange = (index, field, nextValue) => {
    setFormData((prev) => {
      const nextSlabs = [...(prev.value.slabs || [])];
      nextSlabs[index] = { ...nextSlabs[index], [field]: nextValue };
      return {
        ...prev,
        value: {
          ...prev.value,
          slabs: nextSlabs,
        },
      };
    });
    setFormError('');
  };

  const addSlabRow = () => {
    setFormData((prev) => ({
      ...prev,
      value: {
        ...prev.value,
        slabs: [...(prev.value.slabs || []), buildDefaultSlab()],
      },
    }));
  };

  const removeSlabRow = (index) => {
    setFormData((prev) => ({
      ...prev,
      value: {
        ...prev.value,
        slabs: (prev.value.slabs || []).filter((_, slabIndex) => slabIndex !== index),
      },
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (isSaving) return;
    setFormError('');

    if (slabValidation.errors.length > 0) {
      setFormError(slabValidation.errors[0]);
      return;
    }

    const nextData = {
      ...formData,
      value: {
        ...formData.value,
      },
    };

    if (formData.type === constants.PROMOTION_TYPES.SLAB) {
      nextData.value.slabs = (formData.value.slabs || []).map((slab) => ({
        ...slab,
        maxThreshold: slab.maxThreshold === '' ? null : slab.maxThreshold,
      }));
    } else {
      delete nextData.value.slabBasis;
      delete nextData.value.slabs;
    }

    if (
      formData.type === constants.PROMOTION_TYPES.MULTI_BUY
      && formData.value.multiBuyDiscountType === 'BUY_X_GET_Y'
    ) {
      const match = buyGetValue.trim().match(/^(\d+)\s*\+\s*(\d+)$/);
      if (!match) {
        setFormError('Enter Buy + Get as two numbers, e.g. 2+1.');
        return;
      }
      const buyQty = Number(match[1]);
      const getQty = Number(match[2]);
      if (!buyQty || !getQty) {
        setFormError('Buy and Get quantities must be greater than 0.');
        return;
      }
      nextData.value.multiBuyBuyQuantity = buyQty;
      nextData.value.multiBuyGetQuantity = getQty;
    }

    onSave(nextData);
  };

  const renderValueFields = () => {
    switch (formData.type) {
      case constants.PROMOTION_TYPES.PERCENTAGE_ITEM:
      case constants.PROMOTION_TYPES.PERCENTAGE_ORDER:
      case constants.PROMOTION_TYPES.FIXED_AMOUNT_ITEM:
      case constants.PROMOTION_TYPES.FIXED_AMOUNT_ORDER:
        return (
          <div>
            <label>Discount Value ({formData.type.includes('PERCENTAGE') ? '%' : '$'})</label>
            <input
              type="number"
              name="discountValue"
              value={formData.value.discountValue || ''}
              onChange={(event) => handleNestedChange('value', event)}
              className="p-2 border rounded w-full"
              required
            />
          </div>
        );
      case constants.PROMOTION_TYPES.MULTI_BUY:
        return (
          <div className="space-y-4">
            {formData.value.multiBuyDiscountType !== 'BUY_X_GET_Y' && (
              <div>
                <label>Quantity</label>
                <input
                  type="number"
                  name="multiBuyQuantity"
                  value={formData.value.multiBuyQuantity || ''}
                  onChange={(event) => handleNestedChange('value', event)}
                  className="p-2 border rounded w-full"
                  required
                />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700">Discount Type</label>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:gap-4">
                <label><input type="radio" name="multiBuyDiscountType" value="FIXED_PRICE" checked={formData.value.multiBuyDiscountType === 'FIXED_PRICE'} onChange={(event) => handleNestedChange('value', event)} className="mr-1" /> Fixed Price for Bundle</label>
                <label><input type="radio" name="multiBuyDiscountType" value="PERCENTAGE" checked={formData.value.multiBuyDiscountType === 'PERCENTAGE'} onChange={(event) => handleNestedChange('value', event)} className="mr-1" /> Percentage Off Bundle</label>
                <label><input type="radio" name="multiBuyDiscountType" value="BUY_X_GET_Y" checked={formData.value.multiBuyDiscountType === 'BUY_X_GET_Y'} onChange={(event) => handleNestedChange('value', event)} className="mr-1" /> Buy X Get Y</label>
              </div>
            </div>
            {formData.value.multiBuyDiscountType === 'FIXED_PRICE' && (
              <div>
                <label>For Price ($)</label>
                <input
                  type="number"
                  name="multiBuyPrice"
                  value={formData.value.multiBuyPrice || ''}
                  onChange={(event) => handleNestedChange('value', event)}
                  className="p-2 border rounded w-full"
                  required
                />
              </div>
            )}
            {formData.value.multiBuyDiscountType === 'PERCENTAGE' && (
              <div>
                <label>Discount Value (%)</label>
                <input
                  type="number"
                  name="multiBuyDiscountValue"
                  value={formData.value.multiBuyDiscountValue || ''}
                  onChange={(event) => handleNestedChange('value', event)}
                  className="p-2 border rounded w-full"
                  required
                />
              </div>
            )}
            {formData.value.multiBuyDiscountType === 'BUY_X_GET_Y' && (
              <div>
                <label>Buy + Get (e.g., 2+1)</label>
                <input
                  type="text"
                  value={buyGetValue}
                  onChange={(event) => {
                    setBuyGetValue(event.target.value);
                    setFormError('');
                  }}
                  className="p-2 border rounded w-full"
                  inputMode="numeric"
                  required
                />
              </div>
            )}
          </div>
        );
      case constants.PROMOTION_TYPES.SLAB:
        return (
          <div className="space-y-4 border rounded-xl p-4 bg-slate-50">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700">Slab Basis</label>
                <select
                  name="slabBasis"
                  value={formData.value.slabBasis || 'ORDER_VALUE'}
                  onChange={(event) => handleNestedChange('value', event)}
                  className="mt-1 p-2 border rounded w-full"
                >
                  <option value="ORDER_VALUE">Order Value</option>
                  <option value="ITEM_QUANTITY">Item Quantity</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Live Preview Input</label>
                <input
                  type="number"
                  value={slabPreviewInput}
                  onChange={(event) => setSlabPreviewInput(event.target.value)}
                  className="mt-1 p-2 border rounded w-full"
                  min="0"
                />
              </div>
            </div>

            <p className="text-xs text-gray-500">
              Each slab covers Min up to (but not including) Max, so the next slab can start at
              the same number with no gap and no overlap — e.g. 0–150 then 150–300 means $149.50
              lands in the first slab and $150.00 in the second. Leave Max blank for &ldquo;and above&rdquo;.
              Decimals are allowed.
            </p>

            <div className="space-y-3">
              {(formData.value.slabs || []).map((slab, index) => (
                <div key={`slab-${index}`} className="rounded-xl border bg-white p-3 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-semibold text-sm text-slate-900">Slab {index + 1}</p>
                    {(formData.value.slabs || []).length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSlabRow(index)}
                        className="text-sm text-rose-600 hover:text-rose-700"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                    <div>
                      <label className="block text-xs font-medium text-gray-600">Min</label>
                      <input
                        type="number"
                        value={slab.minThreshold}
                        min="0"
                        step="0.01"
                        onChange={(event) => handleSlabChange(index, 'minThreshold', event.target.value)}
                        className="mt-1 p-2 border rounded w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600">Max</label>
                      <input
                        type="number"
                        value={slab.maxThreshold}
                        min="0"
                        step="0.01"
                        onChange={(event) => handleSlabChange(index, 'maxThreshold', event.target.value)}
                        className="mt-1 p-2 border rounded w-full"
                        placeholder="Blank = and above"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600">Reward Type</label>
                      <select
                        value={slab.rewardType}
                        onChange={(event) => handleSlabChange(index, 'rewardType', event.target.value)}
                        className="mt-1 p-2 border rounded w-full"
                      >
                        <option value="PERCENTAGE">Percentage</option>
                        <option value="FIXED_AMOUNT">Fixed Amount</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor={`slab-reward-${index}`} className="block text-xs font-medium text-gray-600">Reward Value</label>
                      <input
                        id={`slab-reward-${index}`}
                        type="number"
                        value={slab.rewardValue}
                        min="0"
                        step="0.01"
                        onChange={(event) => handleSlabChange(index, 'rewardValue', event.target.value)}
                        className="mt-1 p-2 border rounded w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600">Application</label>
                      <select
                        value={slab.rewardApplication || 'PER_ORDER'}
                        onChange={(event) => handleSlabChange(index, 'rewardApplication', event.target.value)}
                        className="mt-1 p-2 border rounded w-full"
                        disabled={slab.rewardType === 'PERCENTAGE'}
                      >
                        <option value="PER_ORDER">Per Order</option>
                        <option value="PER_UNIT">Per Unit</option>
                      </select>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">
                    Range {formatThresholdRange(slab)}: {formatSlabReward(slab)}
                  </p>
                </div>
              ))}
              <button
                type="button"
                onClick={addSlabRow}
                className="rounded-md border border-dashed border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
              >
                Add Slab Row
              </button>
            </div>

            {slabValidation.gapMessages.length > 0 && (
              <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 space-y-1">
                {slabValidation.gapMessages.map((message) => (
                  <p key={message}>{message}</p>
                ))}
              </div>
            )}

            <div className="rounded border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700">
              {slabPreview ? (
                <p>
                  At {formData.value.slabBasis === 'ITEM_QUANTITY' ? `${slabPreviewInput} items` : `$${Number(slabPreviewInput || 0).toFixed(2)}`}:
                  {' '}
                  {formatSlabReward(slabPreview.matched)}
                  {' '}
                  for an estimated discount of
                  {' '}
                  <span className="font-semibold">${Number(slabPreview.discountAmount || 0).toFixed(2)}</span>.
                </p>
              ) : (
                <p>No matching slab for the preview value. This is treated as a valid no-discount gap.</p>
              )}
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const renderApplicationModeField = () => {
    const modes = constants.PROMOTION_APPLICATION_MODES || {
      USER_DRIVEN: 'USER_DRIVEN',
      AUTOMATIC: 'AUTOMATIC',
    };
    return (
      <fieldset disabled={isSaving} className="border-t pt-4">
        <legend className="font-semibold mb-2">Checkout Application</legend>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-6">
          <label className="inline-flex min-h-[44px] items-center cursor-pointer">
            <input
              type="radio"
              name="applicationMode"
              value={modes.AUTOMATIC}
              checked={formData.applicationMode === modes.AUTOMATIC}
              onChange={handleChange}
              className="mr-2"
            />
            Automatic
          </label>
          <label className="inline-flex min-h-[44px] items-center cursor-pointer">
            <input
              type="radio"
              name="applicationMode"
              value={modes.USER_DRIVEN}
              checked={formData.applicationMode !== modes.AUTOMATIC}
              onChange={handleChange}
              className="mr-2"
            />
            User Driven
          </label>
        </div>
      </fieldset>
    );
  };

  const showProductSelector = formData.conditions.appliesTo === 'SPECIFIC_PRODUCTS';
  const allowEntireOrder = isFlatOrderType || (formData.type === constants.PROMOTION_TYPES.SLAB && formData.value.slabBasis !== 'ITEM_QUANTITY');

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <input name="name" value={formData.name} onChange={handleChange} placeholder="Promotion Name" required className="p-2 border rounded w-full" />
      <select name="type" value={formData.type} onChange={handleChange} className="p-2 border rounded w-full">
        {Object.entries(constants.PROMOTION_TYPES).map(([key, value]) => (
          <option key={key} value={value}>{key.replace(/_/g, ' ')}</option>
        ))}
      </select>
      {formError && (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {formError}
        </div>
      )}
      {slabValidation.errors.length > 0 && formData.type === 'SLAB' && (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 space-y-1">
          {slabValidation.errors.map((error) => (
            <p key={error}>{error}</p>
          ))}
        </div>
      )}

      {renderValueFields()}
      {renderApplicationModeField()}

      <div className="border-t pt-4 space-y-4">
        <h3 className="font-semibold">Conditions</h3>
        <select
          name="appliesTo"
          value={formData.conditions.appliesTo}
          onChange={(event) => handleNestedChange('conditions', event)}
          disabled={isFlatOrderType}
          className="p-2 border rounded w-full disabled:bg-gray-100"
        >
          <option value="SPECIFIC_PRODUCTS">Specific Products</option>
          <option value="SPECIFIC_CATEGORIES">Specific Categories</option>
          {allowEntireOrder && <option value="ENTIRE_ORDER">Entire Order</option>}
        </select>

        <div>
          <label>Minimum Purchase Amount</label>
          <input
            type="number"
            name="minPurchaseAmount"
            value={formData.conditions.minPurchaseAmount ?? 0}
            onChange={(event) => handleNestedChange('conditions', event)}
            className="p-2 border rounded w-full"
            min="0"
          />
        </div>

        {formData.conditions.appliesTo === 'SPECIFIC_CATEGORIES' && (
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">Categories</label>
              <select
                multiple
                value={selectedCategories}
                onChange={(event) => handleConditionMultiSelect('categories', event)}
                className="mt-1 h-32 w-full rounded border p-2"
              >
                {categoryOptions.map((category) => (
                  <option key={category._id || category.name} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </select>
              {categoryLoading && <p className="mt-1 text-sm text-gray-500">Loading categories...</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Subcategories</label>
              <select
                multiple
                value={selectedSubcategories}
                onChange={(event) => handleConditionMultiSelect('subcategories', event)}
                disabled={selectedCategories.length === 0 || subcategoryOptions.length === 0}
                className="mt-1 h-32 w-full rounded border p-2 disabled:bg-gray-100"
              >
                {subcategoryOptions.map((subcategory) => (
                  <option key={subcategory} value={subcategory}>
                    {subcategory}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Leave blank to include every subcategory in the selected categories.
              </p>
            </div>
          </div>
        )}

        {showProductSelector && (
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">Select Products</label>
            <input
              type="text"
              value={productQuery}
              onChange={(event) => setProductQuery(event.target.value)}
              placeholder="Search by name, SKU, or barcode..."
              className="w-full p-2 border rounded"
            />
            {!activeStoreId && (
              <p className="text-sm text-amber-600">Select a store to search products.</p>
            )}
            {selectedProducts.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedProducts.map((product) => (
                  <button
                    key={product._id}
                    type="button"
                    onClick={() => toggleProductSelection(product)}
                    className="px-3 py-1 rounded-full text-xs font-semibold bg-primary/10 text-primary hover:bg-primary/20"
                  >
                    {product.name} x
                  </button>
                ))}
              </div>
            )}
            <div className="border rounded max-h-56 overflow-y-auto">
              {productQuery.trim().length === 0 ? (
                <p className="p-3 text-sm text-gray-500">Type to search products.</p>
              ) : productLoading ? (
                <p className="p-3 text-sm text-gray-500">Searching...</p>
              ) : !activeStoreId ? (
                <p className="p-3 text-sm text-gray-500">Select a store to search products.</p>
              ) : productResults.length === 0 ? (
                <p className="p-3 text-sm text-gray-500">No products found.</p>
              ) : (
                productResults.map((product) => (
                  <label
                    key={product._id}
                    className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                  >
                    <input
                      type="checkbox"
                      checked={formData.conditions.productIds.includes(product._id)}
                      onChange={() => toggleProductSelection(product)}
                    />
                    <span className="font-medium text-gray-900">{product.name}</span>
                    <span className="text-xs text-gray-500">SKU: {product.sku || '-'}</span>
                    <span className="text-xs text-gray-400">Barcode: {product.barcode || '-'}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 border-t pt-4 sm:grid-cols-2">
        <div>
          <label>Start Date</label>
          <input
            type="date"
            name="startDate"
            value={formData.startDate}
            onChange={handleChange}
            className="p-2 border rounded w-full"
          />
        </div>
        <div>
          <label>End Date (optional)</label>
          <input
            type="date"
            name="endDate"
            value={formData.endDate}
            onChange={handleChange}
            className="p-2 border rounded w-full"
          />
        </div>
      </div>

      <div>
        <label>Assign to Stores (optional, leave blank for all)</label>
        <select multiple value={formData.stores} onChange={(event) => handleMultiSelect('stores', event)} className="w-full h-24 p-2 border rounded">
          {stores.map((store) => <option key={store._id} value={store._id}>{store.name}</option>)}
        </select>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
        <label><input type="checkbox" name="isActive" checked={formData.isActive} onChange={handleChange} className="mr-2" /> Active</label>
        <label><input type="checkbox" name="isStackable" checked={formData.isStackable} onChange={handleChange} className="mr-2" /> Stackable</label>
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t mt-4">
        <button type="button" onClick={onCancel} disabled={isSaving} className="bg-gray-200 text-gray-700 px-4 py-2 min-h-[44px] rounded-md disabled:opacity-50">Cancel</button>
        <button type="submit" disabled={isSaving} className="bg-primary text-white px-4 py-2 min-h-[44px] rounded-md disabled:opacity-50">{isSaving ? 'Saving…' : 'Save Promotion'}</button>
      </div>
    </form>
  );
};

function prevTypeRequiresScope(type, slabBasis) {
  if (type === 'SLAB') {
    return slabBasis === 'ITEM_QUANTITY';
  }
  return !['PERCENTAGE_ORDER', 'FIXED_AMOUNT_ORDER'].includes(type);
}

export default PromotionForm;
